import { Back, Power3, TimelineLite } from "gsap";
import * as HLF from "html-living-framework";
import { Application } from "../Application";
import * as Constants from "../Data/Constants";
import { Culture } from "../Data/Culture";
import * as Topics from "../Data/Topics";
import { Button } from "./Button";
import { InteruptType, VoiceCategory } from "./VoiceOverManager";

const BACKGROUND_IMAGE_PREFIX = "button-sample-";

const FONT_SIZE_NORMAL = 36;
const FONT_SIZE_SMALL = 30;
export class InstrumentKey extends Button {
    protected longPressTimeout?: number;
    protected _instrumentNameVO: string | undefined;

    public set interactive(value: boolean) {
        this._interactive = value;
        if (!value) {
            this.cancelLongPress();
        }
    }

    public get interactive() {
        return this._interactive;
    }

    public set culture(pCulture: Culture) {
        const cultureAsString = HLF.NumberUtils.addZeroPadding(pCulture, 2);
        const textureName = BACKGROUND_IMAGE_PREFIX + cultureAsString;
        this._image!.texture = HLF.PixiUtils.getTexture(textureName, Constants.SPRITESHEET_INSTRUMENT_KEYBOARD);
        this._image!.scale.set(1, 1);
        this._image!.alpha = 1;
        this._bg2.alpha = 0;
        this.cancelLongPress();
    }

    public get iconSize(): PIXI.Point {
        // apparently the PIXI.Point constructor doesn't mind undefined arguments
        return new PIXI.Point(this._image?.texture.width, this._image?.texture.height);
    }

    public get iconWidth(): number {
        return this._image?.texture.width ?? 0;
    }

    public get iconHeight(): number {
        return this._image?.texture.height ?? 0;
    }
    protected _cultureChangeTimeline?: TimelineLite;
    /** Duplicate of background image, for crossfading */
    private readonly _bg2: PIXI.Sprite;
    private _interactive: boolean = true;

    constructor(
        pCallback: () => void,
        pForegroundSprite: PIXI.Sprite,
        pCulture: Culture,
        pHoverVO: string,
        pClickedSFX: string,
        pLabel: string,
        pVolume: number,
    ) {
        super(
            // tslint:disable-next-line: no-empty
            pCallback,
            BACKGROUND_IMAGE_PREFIX + HLF.NumberUtils.addZeroPadding(pCulture, 2),
            Constants.SPRITESHEET_INSTRUMENT_KEYBOARD,
            pHoverVO,
            pClickedSFX,
        );

        this.name = `${pLabel} (${Culture[pCulture]})`;
        this._icon = pForegroundSprite;
        this._icon.anchor.copy(this._icon.texture.defaultAnchor);

        const container = new PIXI.Container();
        this.addChild(container);
        this._image!.setParent(container);
        container.addChild(this._icon);
        this.setVisuals(container);
        this._bg2 = HLF.PixiUtils.makeSprite(
            BACKGROUND_IMAGE_PREFIX + HLF.NumberUtils.addZeroPadding(pCulture, 2),
            Constants.SPRITESHEET_INSTRUMENT_KEYBOARD,
        );
        this._bg2.scale.set(0.5, 0.5);
        this._bg2.alpha = 0;
        container.addChildAt(this._bg2, 1);

        const fontSize = (pLabel.length > 7) ? FONT_SIZE_SMALL : FONT_SIZE_NORMAL;
        this.addText(pLabel, Constants.FONT_OUTLINED, fontSize, HLF.Colour.WHITE.toHex());
        this._text!.letterSpacing = -3;
        this._text!.anchor = new PIXI.Point(0.5, 1);
        this._text!.position.set(0, (this._image!.height / 2) - 5);
        this._text!.interactive = true;
        this._text!.on(HLF.Input.Events.POINTER_TAP, this.playInstrumentNameVO.bind(this));

        this._clickedSFXVolume = pVolume;
        this._instrumentNameVO = this._hoverVO;

        PubSub.subscribe(Topics.GAME_PAUSED, this.onPause.bind(this));
        PubSub.subscribe(Topics.GAME_UNPAUSED, this.onResume.bind(this));

        this.on(HLF.Input.Events.POINTER_UP_OUTSIDE, this.onPointerUp.bind(this));
        this.on(HLF.Input.Events.TOUCH_CANCEL, this.onPointerOut.bind(this));
    }

    public switchInstrument(pNewSFX: string, pVolume?: number): void {
        this._clickedSFX = pNewSFX;
        this._clickedSFXVolume = pVolume || this._clickedSFXVolume;
    }

    public getFocusSize(): PIXI.Point {
        return new PIXI.Point(this.width * 0.8, this.height * 0.8);
    }

    public getOnboardingPosition() {
        return this.toGlobal(new PIXI.Point(0, -this.height / 2));
    }

    public onFocusActivated(): void {
        super.onFocusActivated();
        PubSub.publish(Topics.SAMPLE_PAD_BUTTON_CLICKED, this);
    }
    /**
     * Play the culture switch animation, with optional delay
     * @param pCulture Culture to switch to
     * @param pIcon Foreground icon (the instrument)
     * @param pText Name of the instrument
     * @param pAudioFilename Audio filename (without extension)
     * @param pHoverVO Hover voiceover audio filename (without extension)
     * @param pDelay Delay in seconds before doing the switch animation
     */
    public switchCulture(
        pCulture: Culture,
        pText: string,
        pIcon: PIXI.Texture,
        pAudioFilename: string,
        pAudioVolume: number,
        pHoverVO: string,
        pDelay: number,
    ): void {
        this.name = `${pText} (${Culture[pCulture]})`;
        const cultureAsString = HLF.NumberUtils.addZeroPadding(pCulture, 2);
        const textureName = BACKGROUND_IMAGE_PREFIX + cultureAsString;
        this._bg2.alpha = 0;
        this._bg2.texture = HLF.PixiUtils.getTexture(textureName, Constants.SPRITESHEET_INSTRUMENT_KEYBOARD);
        this._bg2.scale.set(0.5, 0.5);

        if (this._cultureChangeTimeline !== undefined) {
            this._cultureChangeTimeline.kill();
        }
        this._cultureChangeTimeline = new TimelineLite();
        this._cultureChangeTimeline
            .to(this, 0.3, {pixi: {scaleX: 0.5, scaleY: 0.5}, ease: Back.easeIn.config(4)})
            .to(this._image!, 0.3, {pixi: {alpha: 0, scaleX: 0.5, scaleY: 0.5}, ease: Back.easeIn.config(4)}, 0.1)
            .to(this._bg2, 0.2, {pixi: {alpha: 1, scaleX: 1, scaleY: 1}, ease: Power3.easeOut}, 0.3)
            .call(() => this.setText(pText), [], this, 0.2)
            .call(() => this.switchInstrument(pAudioFilename, pAudioVolume), [], this, 0.2)
            .call(() => {
                this._icon!.texture = pIcon.clone();
                this._icon!.anchor.copy(this._icon!.texture.defaultAnchor);
            }, [], this, 0.2)
            .to(this, 0.5, {pixi: {scaleX: 0.9, scaleY: 0.9}, ease: Back.easeOut.config(4)}, 0.3)
            .call(() => this._cultureChangeTimeline = undefined)
            .delay(pDelay)
        ;

        this._hoverVO = this._instrumentNameVO = pHoverVO;
    }

    public setText(pText: string): void {
        super.setText(pText);
        if (this._text !== undefined) {
            const fontSize = (pText.length > 7) ? FONT_SIZE_SMALL : FONT_SIZE_NORMAL;
            this._text!.font = fontSize + "px " + Constants.FONT_OUTLINED;
        }
    }
    protected onPointerDown(pEvent: PIXI.interaction.InteractionEvent): void {
        super.onPointerDown(pEvent); // this calls playClickedSFX()
        this.cancelLongPress();
        this.longPressTimeout = setInterval(() => this.playClickedSFX(), 250);
    }

    protected onPointerUp(pEvent: PIXI.interaction.InteractionEvent): void {
        this.cancelLongPress();
        super.onPointerUp(pEvent);
    }

    protected onPointerOut(pEvent: PIXI.interaction.InteractionEvent): void {
        this.cancelLongPress();
        super.onPointerOut(pEvent);
    }

    protected playClickedSFX() {
        super.playClickedSFX();
        PubSub.publish(Topics.SAMPLE_PAD_BUTTON_CLICKED, this);
        const newFilename = this._clickedSFX!.replace(/(\d+)$/, (match) => {
            let variant = parseInt(match, 10) + 1;
            if (variant >= 3) {
                variant = 0;
            }
            return variant.toString();
        });
        this.switchInstrument(newFilename);
    }

    protected playHoverVO(): boolean {
        const didItWork = super.playHoverVO();
        if (didItWork) {
            this._hoverVO = undefined;
        }
        return didItWork;
    }

    protected playInstrumentNameVO() {
        if (this._instrumentNameVO !== undefined) {
            Application.instance.voiceOver.play(this._instrumentNameVO, InteruptType.EQUAL, VoiceCategory.UI);
        }
    }

    protected onPause() {
        if (this._cultureChangeTimeline !== undefined) {
            this._cultureChangeTimeline.pause();
        }
    }
    protected onResume() {
        if (this._cultureChangeTimeline !== undefined) {
            this._cultureChangeTimeline.resume();
        }
    }

    private cancelLongPress() {
        if (this.longPressTimeout) {
            clearInterval(this.longPressTimeout);
            this.longPressTimeout = undefined;
        }
    }
}
